import type { RuleGroupView } from '@tabel/shared/schemas/rules';
import { Plus } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { Badge, Button } from '../../components/ui';
import { DescribeRule } from './DescribeRule';
import { GuardedModal } from './GuardedModal';
import { type Values, fieldIndex } from './answer';
import { NEED_TEXT, addValues, addedInDraft, catalogOf, catalogSentence } from './catalog';
import { LABEL, T } from './texts';

/*
 * Окно «Добавить правило» (004, US6, T100; contracts/ui.md, «Окна»; FR-054, FR-055, FR-057; сценарии US6 1–3, 5):
 * - «Можно добавить» — у правила вопрос, одно предложение, что оно делает, и «Добавить»; правило, которое уже
 *   включено в черновике, — с меткой «В черновике»;
 * - «Сначала нужны графики работы», «Сначала нужен производственный календарь» и другие — только если такие правила
 *   есть; вместо «Добавить» — что нужно сначала;
 * - «Нет нужного правила?» — просьба команде разработки.
 * «Добавить» отдаёт странице правило и значения, с которыми оно откроется в изменении в своём разделе (FR-055).
 */

function Rule({ g, sentence, aside }: { g: RuleGroupView; sentence: string; aside: ReactNode }) {
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 py-3 first:pt-1">
      <div className="min-w-0 flex-1 basis-[260px] space-y-0.5">
        <p className="text-[14px] font-medium break-words text-ink">{g.question}</p>
        {sentence && <p className="text-[13px] break-words text-ink-2">{sentence}</p>}
      </div>
      <div className="shrink-0 max-sm:w-full max-sm:[&>button]:w-full">{aside}</div>
    </li>
  );
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section aria-label={label} className="space-y-1">
      <h3 className="text-[12px] font-semibold tracking-[0.07em] text-ink-3 uppercase">{label}</h3>
      <ul className="divide-y divide-line">{children}</ul>
    </section>
  );
}

export function CatalogDialog({
  open,
  groups,
  onClose,
  onAdd,
}: {
  open: boolean;
  /** Правила версии, от которой строится изменение (черновик или последняя опубликованная). */
  groups: readonly RuleGroupView[];
  onClose: () => void;
  /** «Добавить»: правило и значения, с которыми оно открывается в изменении. */
  onAdd: (groupId: string, seed: Values) => void;
}) {
  const fields = useMemo(() => fieldIndex(groups), [groups]);
  const catalog = useMemo(() => catalogOf(groups), [groups]);
  const empty = catalog.can.length === 0 && catalog.needs.length === 0;
  return (
    <GuardedModal
      open={open}
      onClose={onClose}
      title={LABEL.addRule}
      description={T.catalogIntro}
      width="w-[720px]"
    >
      {(ctl) => (
        <div className="space-y-6 text-[13.5px]">
          {empty && <p className="text-ink-2">{T.catalogEmpty}</p>}
          {catalog.can.length > 0 && (
            <Group label={LABEL.catalogCan}>
              {catalog.can.map((g) => (
                <Rule
                  key={g.id}
                  g={g}
                  sentence={catalogSentence(g, fields)}
                  aside={
                    addedInDraft(g) ? (
                      <Badge tone="accent">{LABEL.inDraft}</Badge>
                    ) : (
                      <Button variant="primary" icon={Plus} onClick={() => onAdd(g.id, addValues(g) ?? {})}>
                        {LABEL.add}
                      </Button>
                    )
                  }
                />
              ))}
            </Group>
          )}
          {catalog.needs.map(({ need, groups: list }) => (
            <Group key={need} label={NEED_TEXT[need].group}>
              {list.map((g) => (
                <Rule
                  key={g.id}
                  g={g}
                  sentence={catalogSentence(g, fields)}
                  aside={<p className="text-[13px] text-ink-2">{NEED_TEXT[need].rule}</p>}
                />
              ))}
            </Group>
          ))}
          <div className="border-t border-line pt-4">
            <DescribeRule
              onDirty={ctl.setDirty}
              onSent={() => {
                ctl.setDirty(false);
                onClose();
              }}
            />
          </div>
          <div className="flex justify-end max-sm:[&>button]:flex-auto">
            <Button onClick={ctl.requestClose}>{LABEL.close}</Button>
          </div>
        </div>
      )}
    </GuardedModal>
  );
}
